import Head from 'expo-router/head';

import { listSeedScript } from '@/components/detail/list-seed';
import type { BoatTypePage } from '@/lib/boat-rental';
import { isBoatDelisted, isBoatIndexable, type Boat } from '@todo/core/boats';
import {
  localizedBoatDescription,
  localizedBoatMetaDescription,
  localizedBoatTitle,
  localizedBoatTitleParts,
} from '@/lib/i18n/boat-content';
import { boatRentalCityCopy, boatTypePageTitle } from '@/lib/i18n/boat-rental-content';
import type { Locale } from '@todo/core/i18n/locales';
import { BOAT_TYPE_PAGES_SEED_KEY } from '@todo/core/list-queries';
import {
  absoluteUrl,
  boatsCrumb,
  breadcrumbJsonLd,
  hreflangLinks,
  itemListJsonLd,
  merchantReturnPolicy,
  offerShippingDetails,
  ogLocale,
  richResultImage,
  seoTitle,
  truncate,
} from '@todo/core/seo';
import { prefetchElementId, serializePrefetch } from '@todo/core/static-prefetch';
import { SITE } from '@/site';

// Display name for the supplier a boat is booked through, keyed by
// `boats.source` (currently only 'click_and_boat').
const SOURCE_BRAND: Record<string, string> = { click_and_boat: 'Click&Boat' };

// schema.org/Product structured data — rating + price make boat pages
// eligible for rich results, mirroring tour-head.tsx's tourJsonLd.
function boatJsonLd(boat: Boat, url: string, name: string, description: string) {
  const brand = SOURCE_BRAND[boat.source];
  const image = richResultImage(boat.image_url);
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    url,
    sku: boat.source_id,
    ...(brand ? { brand: { '@type': 'Brand', name: brand } } : {}),
    ...(image ? { image: [image] } : {}),
    description: truncate(description, 500),
    // No aggregateRating: schema.org requires a real ratingCount/reviewCount
    // alongside ratingValue, and Click&Boat's catalog gives us a rating with
    // no review count (unlike Viator) — fabricating a count would be exactly
    // the kind of manipulated structured data Google's guidelines warn against.
    ...(boat.from_price != null
      ? {
          offers: {
            '@type': 'Offer',
            price: boat.from_price,
            priceCurrency: boat.currency ?? SITE.currency,
            availability: 'https://schema.org/InStock',
            ...(boat.booking_url ? { url: boat.booking_url } : {}),
            // Merchant-listing fields Google flags as missing on any priced
            // Product offer — a booking, so free "shipping" and no returns,
            // as for tours. See the helpers in @todo/core/seo.
            shippingDetails: offerShippingDetails(boat.currency ?? SITE.currency),
            hasMerchantReturnPolicy: merchantReturnPolicy(),
          },
        }
      : {}),
  };
}

// Per-boat <Head>, shared by the native and web variants of
// app/[lang]/boats/[id] so the SEO markup has a single source of truth.
// Mirrors TourHead. No FAQPage JSON-LD: the site-wide boat FAQ was identical on
// every boat page, so it now lives only on the /boats hub, and each detail page
// links to its city's /boat-rental/[city] page (with a per-city FAQ) instead.
export function BoatHead({
  boat,
  locale,
  related,
  relatedSeedKey,
  hasCityPage = false,
  typePage = null,
}: {
  boat: Boat;
  locale: Locale;
  related?: Boat[];
  relatedSeedKey?: string;
  /** The boat's city has a /boat-rental/[city] page — see useRelatedBoats. */
  hasCityPage?: boolean;
  /** The boat's /boat-rental/[city]/[type] page, if exported — see useRelatedBoats. */
  typePage?: BoatTypePage | null;
}) {
  const routePath = `boats/${boat.id}`;
  const canonical = absoluteUrl(locale, routePath);
  const title = localizedBoatTitle(boat, locale);
  const titleParts = localizedBoatTitleParts(boat, locale);
  const description = localizedBoatDescription(boat, locale);
  const metaDescription = localizedBoatMetaDescription(boat, locale);
  const cityCopy = hasCityPage && boat.city_id ? boatRentalCityCopy(locale, boat.city_id) : undefined;
  const cityCrumb = cityCopy ? [{ name: cityCopy.title, path: `boat-rental/${boat.city_id}` }] : [];
  const typeTitle =
    cityCopy && typePage && boat.city_id ? boatTypePageTitle(locale, typePage, boat.city_id) : undefined;
  const typeCrumb = typeTitle
    ? [{ name: typeTitle, path: `boat-rental/${boat.city_id}/${typePage}` }]
    : [];

  return (
    <Head key={`${locale}:${boat.id}`}>
      {/* The suffix goes in as seoTitle's qualifier, which is never cut — it's
          what keeps this <title> distinct from other boats'. */}
      <title>{seoTitle(titleParts.base, titleParts.suffix)}</title>
      <meta name="description" content={metaDescription} />
      {/* Boilerplate-only and delisted boats: kept out of the index (and
          sitemap.xml), but their links stay followed — see isBoatIndexable. */}
      {isBoatIndexable(boat) ? null : <meta name="robots" content="noindex, follow" />}
      <link rel="canonical" href={canonical} />
      {hreflangLinks(routePath).map((link) => (
        <link key={link.hrefLang} rel="alternate" hrefLang={link.hrefLang} href={link.href} />
      ))}
      <meta property="og:type" content="product" />
      <meta property="og:locale" content={ogLocale(locale)} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonical} />
      {boat.image_url ? <meta property="og:image" content={boat.image_url} /> : null}
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={metaDescription} />
      {boat.image_url ? <meta name="twitter:image" content={boat.image_url} /> : null}
      {/* No Product/Offer for a delisted boat — it can't be booked any more. */}
      {isBoatDelisted(boat) ? null : (
        <script type="application/ld+json">
          {JSON.stringify(boatJsonLd(boat, canonical, title, description))}
        </script>
      )}
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbJsonLd(locale, [boatsCrumb(locale), ...cityCrumb, ...typeCrumb, { name: title }]))}
      </script>
      {related && related.length > 0 ? (
        <script type="application/ld+json">
          {JSON.stringify(
            itemListJsonLd(
              related.map((r) => ({
                name: localizedBoatTitle(r, locale),
                url: absoluteUrl(locale, `boats/${r.id}`),
              })),
              canonical,
            ),
          )}
        </script>
      ) : null}
      {/* Baked record so the first client render matches this static HTML and
          hydration doesn't mismatch (#418). */}
      <script id={prefetchElementId('boat', boat.id)} type="application/json">
        {serializePrefetch(boat)}
      </script>
      {relatedSeedKey ? listSeedScript(relatedSeedKey) : null}
      {/* Which type pages exist — useRelatedBoats reads it for the type link. */}
      {listSeedScript(BOAT_TYPE_PAGES_SEED_KEY)}
    </Head>
  );
}
